import React, { createContext, useEffect, useState, ReactNode } from "react";

export interface EmbedOptions {
  showActions: boolean;
  showDiscount: boolean;
  showCheckout: boolean;
}

const DEFAULT_EMBED_OPTIONS: EmbedOptions = {
  showActions: false,
  showDiscount: false,
  showCheckout: false,
};

const NON_EMBED_OPTIONS: EmbedOptions = {
  showActions: true,
  showDiscount: true,
  showCheckout: true,
};

interface EmbedContextValue {
  isEmbedded: boolean;
  embedOptions: EmbedOptions;
}

export const EmbedContext = createContext<EmbedContextValue>({
  isEmbedded: false,
  embedOptions: NON_EMBED_OPTIONS,
});

const EMBED_STORAGE_KEY = "laborhack_embed_mode";
const EMBED_OPTIONS_STORAGE_KEY = "laborhack_embed_options";

function parseEmbedOptionsFromURL(): Partial<EmbedOptions> {
  const urlParams = new URLSearchParams(window.location.search);
  const options: Partial<EmbedOptions> = {};

  if (urlParams.has("showActions")) {
    options.showActions = urlParams.get("showActions") === "true";
  }
  if (urlParams.has("showDiscount")) {
    options.showDiscount = urlParams.get("showDiscount") === "true";
  }
  if (urlParams.has("showCheckout")) {
    options.showCheckout = urlParams.get("showCheckout") === "true";
  }

  return options;
}

function loadStoredEmbedOptions(): EmbedOptions | null {
  const stored = sessionStorage.getItem(EMBED_OPTIONS_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as EmbedOptions;
    } catch {
      return null;
    }
  }
  return null;
}

interface EmbedProviderProps {
  children: ReactNode;
}

export const EmbedProvider: React.FC<EmbedProviderProps> = ({ children }) => {
  const [isEmbedded, setIsEmbedded] = useState<boolean>(() => {
    // Check sessionStorage first for persisted state
    const storedValue = sessionStorage.getItem(EMBED_STORAGE_KEY);
    if (storedValue === "true") {
      return true;
    }

    // Check URL for embed=true parameter
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("embed") === "true";
  });

  const [embedOptions, setEmbedOptions] = useState<EmbedOptions>(() => {
    if (!isEmbedded) return NON_EMBED_OPTIONS;

    // Check URL params first, fall back to stored options, then defaults
    const urlOptions = parseEmbedOptionsFromURL();
    const storedOptions = loadStoredEmbedOptions();

    return {
      ...DEFAULT_EMBED_OPTIONS,
      ...storedOptions,
      ...urlOptions,
    };
  });

  useEffect(() => {
    // Check URL on mount and when location changes
    const urlParams = new URLSearchParams(window.location.search);
    const embedParam = urlParams.get("embed") === "true";

    if (embedParam) {
      setIsEmbedded(true);
      sessionStorage.setItem(EMBED_STORAGE_KEY, "true");

      // Parse and persist embed options
      const urlOptions = parseEmbedOptionsFromURL();
      const storedOptions = loadStoredEmbedOptions();
      const mergedOptions = {
        ...DEFAULT_EMBED_OPTIONS,
        ...storedOptions,
        ...urlOptions,
      };

      setEmbedOptions(mergedOptions);
      sessionStorage.setItem(
        EMBED_OPTIONS_STORAGE_KEY,
        JSON.stringify(mergedOptions)
      );
    }
  }, []);

  // Apply embed-specific styles (white background, no border)
  useEffect(() => {
    if (isEmbedded) {
      // Use setProperty with 'important' to override !important in CSS
      document.body.style.setProperty("background-color", "#ffffff", "important");
      document.body.style.setProperty("border", "none", "important");
      document.documentElement.style.setProperty("background-color", "#ffffff", "important");
      document.documentElement.style.setProperty("border", "none", "important");
    }

    return () => {
      // Cleanup on unmount (though unlikely in embed mode)
      if (isEmbedded) {
        document.body.style.removeProperty("background-color");
        document.body.style.removeProperty("border");
        document.documentElement.style.removeProperty("background-color");
        document.documentElement.style.removeProperty("border");
      }
    };
  }, [isEmbedded]);

  return (
    <EmbedContext.Provider value={{ isEmbedded, embedOptions }}>
      {children}
    </EmbedContext.Provider>
  );
};
